/**
 * The configured images for one placement, ready to render.
 *
 * What a consuming page wants and nothing else: the ACTIVE images, in the order a Super Admin
 * put them in, each with a usable src. Disabled rows and the ones whose file could not be signed
 * for are dropped here rather than in every component that shows pictures — a carousel that has
 * to remember to filter is a carousel that will one day render a hidden image.
 *
 * ── Failure is an empty list, never a thrown error ────────────────────────────────────────
 *
 * This decorates a page; it is not the page. An unreachable API, a placement nobody has
 * configured yet, an environment where the module is not deployed — all three mean "no images",
 * and a home page that white-screens because a gallery could not load would be a far worse
 * outcome than a home page with no gallery. `error` is reported for anyone who wants to say so,
 * and every caller today simply renders nothing.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { ImagePlacement, ResolvedWebsiteImage } from '../types/websiteImage';
import { WebsiteImageService, resolveImageUrls } from '../services/websiteImageService';

export interface UseWebsiteImages {
  images: ResolvedWebsiteImage[];
  loading: boolean;
  /** Set when the fetch failed. `images` is [] either way, so rendering need not consult it. */
  error: string;
  reload: () => void;
}

export function useWebsiteImages(placement: ImagePlacement | string): UseWebsiteImages {
  const [images, setImages] = useState<ResolvedWebsiteImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  /*
   * Whether this hook's owner is still mounted.
   *
   * Signing URLs is a round trip per image, so a reader who opens Home and navigates away before
   * it finishes is ordinary rather than exceptional.
   */
  const onScreen = useRef(true);
  useEffect(() => {
    onScreen.current = true;
    return () => {
      onScreen.current = false;
    };
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await WebsiteImageService.list(placement);
      const resolved = await resolveImageUrls(rows.filter(row => row.active));
      if (!onScreen.current) return;
      // A row whose file could not be signed for would render as a broken image, which is worse
      // than one fewer picture in a gallery nobody is counting.
      setImages(resolved.filter(row => Boolean(row.url)));
      setError('');
    } catch (err: any) {
      if (!onScreen.current) return;
      console.warn('[useWebsiteImages] could not load', placement, err);
      setImages([]);
      setError(err?.message || 'Images could not be loaded.');
    } finally {
      if (onScreen.current) setLoading(false);
    }
  }, [placement]);

  useEffect(() => {
    load();
  }, [load]);

  return { images, loading, error, reload: load };
}
